/* eslint-disable max-len */
import React, {
  memo, useRef, useState, useEffect,
} from 'react';
import clsx from 'clsx';
import { ChevronLeft } from 'react-feather';
import '../../modal.scss';
import PropTypes from 'prop-types';
import Icon from '../../../icon';
import FeaturedIcon, { FeaturedIconColors, FeaturedIconSizes } from '../../../featured-icon';
import WithOverflowBorders from '../../../with-overflow-borders/with-overflow-borders';
import {
  CloseButton, ButtonIds, ModalTypes, PRIMARY_BUTTON_CLASS, ENTER_KEY_EVENT,
} from '../../constants';
import ButtonWrapper from '../button-wrapper/button-wrapper';
import { ButtonTypes } from '../../../button';

/* eslint-disable react/jsx-props-no-spreading */
const ModalContent = ({
  header, description, buttons, showClose, headerIcons, renderIcon, iconColor, onButtonClick, type,
  children, onHeaderIconClick, onClose, headerLeftAdornments, showBackArrow, onBackArrowClick,
  primaryActionOnEnter,
}) => {
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const [contentMaxHeight, setContentMaxHeight] = useState(0);
  const hasTransitions = type === ModalTypes.WITH_HEIGHT_TRANSITIONS;
  const contentTitleClass = clsx(
    'modal-title',
    { 'modal-header-left-adornments': !!headerLeftAdornments },
  );

  // Max height is required for overflowing content with animate height effect
  const calculateMaxHeight = () => {
    let height = 0;

    if (hasTransitions) {
      const { clientHeight: headerHeight = 0 } = headerRef.current;
      const { clientHeight: footerHeight = 0 } = footerRef.current;
      height = window.innerHeight - 140 - headerHeight - footerHeight;
    }

    setContentMaxHeight(height);
  };

  useEffect(() => {
    calculateMaxHeight();
  }, [type]);

  useEffect(() => {
    if (hasTransitions) {
      window.addEventListener('resize', calculateMaxHeight);
    }

    return () => {
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, []);

  // Click primary button on keyboard enter event
  const handleKeyPress = (event) => {
    if (primaryActionOnEnter && event.key === ENTER_KEY_EVENT && footerRef.current) {
      const buttonRefs = footerRef.current.getElementsByClassName(PRIMARY_BUTTON_CLASS);
      if (buttonRefs.length > 0) {
        buttonRefs[0].click();
      }
    }
  };

  const headerIconButtonClick = (id) => {
    if (id === ButtonIds.CLOSE) {
      onClose();
    } else {
      onHeaderIconClick(id);
    }
  };

  const getIcon = () => (
    <FeaturedIcon
      renderIcon={renderIcon}
      color={iconColor}
      className="modal-icon"
      size={FeaturedIconSizes.LARGE}
    />
  );

  const getHeaderIcons = () => {
    const iconButtons = [...headerIcons];

    const CloseButtonWithRenderIcon = {
      ...CloseButton,
      renderIcon: ({ size, color }) => <Icon icon={CloseButton?.icon} size={size} color={color} />,
    };

    if (showClose) {
      iconButtons.push(CloseButtonWithRenderIcon);
    }

    return (
      <div className="modal-header-icons-cont">
        {iconButtons.map((iconData) => (
          <ButtonWrapper
            borderLess
            key={iconData.id}
            type={ButtonTypes.SECONDARY}
            onClick={headerIconButtonClick}
            renderIcon={iconData?.renderIcon}
            {...iconData}
          />
        ))}
      </div>
    );
  };

  const getButtons = () => (
    <div className="modal-buttons" ref={footerRef}>
      {buttons.map(({ buttonClass, ...rest }) => (
        <ButtonWrapper
          {...rest}
          key={rest.id}
          onClick={onButtonClick}
          className={clsx('modal-button', buttonClass)}
        />
      ))}
    </div>
  );

  const getBackArrow = () => (
    <div className="modal-back-arrow">
      <Icon icon={ChevronLeft} onClick={onBackArrowClick} />
    </div>
  );

  const getHeader = () => (
    <div className="modal-header" ref={headerRef}>
      {renderIcon ? getIcon() : null}
      <div className="modal-header-text">
        <div className={contentTitleClass}>
          {showBackArrow && getBackArrow()}
          {header}
          {headerLeftAdornments}
        </div>
        {description && (
          <div className={clsx('modal-description', { back: showBackArrow })}>
            {description}
          </div>
        )}
      </div>
      {(headerIcons.length > 0 || showClose) && getHeaderIcons()}
    </div>
  );

  const getContent = () => (
    <WithOverflowBorders className="modal-content-wrapper" hasFooterElements={buttons.length > 0}>
      <div
        className="modal-content"
        onKeyPress={handleKeyPress}
        style={hasTransitions ? { maxHeight: contentMaxHeight } : null}
      >
        {children}
      </div>
    </WithOverflowBorders>
  );

  // children used directly without a wrapper to provision custom scroll and overall provide more
  // control over the styles
  return (
    <>
      {getHeader()}
      {children && getContent()}
      {buttons.length ? getButtons() : <div ref={footerRef} className="modal-footer-placeholder" />}
    </>
  );
};

ModalContent.defaultProps = {
  header: '',
  description: '',
  buttons: [],
  headerIcons: [],
  showClose: true,
  renderIcon: null,
  iconColor: null,
  children: null,
  className: '',
  custom: false,
  type: ModalTypes.DEFAULT,
  onButtonClick: () => { },
  onHeaderIconClick: () => { },
  onClose: () => { },
  headerLeftAdornments: null,
  showBackArrow: false,
  onBackArrowClick: () => { },
  primaryActionOnEnter: true,
};

ModalContent.propTypes = {
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  description: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.shape({})),
  headerIcons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    borderLess: PropTypes.bool,
    className: PropTypes.string,
    description: PropTypes.string,
    renderIcon: PropTypes.func,
    type: PropTypes.oneOf(Object.values(ButtonTypes)),
    loading: PropTypes.bool,
  })),
  renderIcon: PropTypes.func,
  showClose: PropTypes.bool,
  iconColor: PropTypes.oneOf(Object.values(FeaturedIconColors)),
  type: PropTypes.oneOf(Object.values(ModalTypes)),
  children: PropTypes.node,
  className: PropTypes.string,
  custom: PropTypes.bool,
  onButtonClick: PropTypes.func,
  onHeaderIconClick: PropTypes.func,
  onClose: PropTypes.func,
  headerLeftAdornments: PropTypes.node,
  showBackArrow: PropTypes.bool,
  onBackArrowClick: PropTypes.func,
  primaryActionOnEnter: PropTypes.bool,
};

export default memo(ModalContent);
